import React, { useCallback, useEffect, useState } from 'react';
import { SignedIn, SignedOut, RedirectToSignIn, UserButton, useAuth } from '@clerk/clerk-react';
import Dashboard from './components/Dashboard';
import FieldMapping from './components/FieldMapping';
import RationalizationWorkbench from './components/RationalizationWorkbench';
import RelationsAuthoring from './components/RelationsAuthoring';
import IngestionExport from './components/IngestionExport';
import PostIngestionValidation from './components/PostIngestionValidation';
import ProjectWorkspace from './components/ProjectWorkspace';
import ProjectActivityBar from './components/ProjectActivityBar';
import SourceControl from './components/SourceControl';
import { apiFetch, configureApiAuth, getProjectId, setProjectId } from './api';


const TABS = [
  { id: 'audit', label: 'Audit' },
  { id: 'mapping', label: 'Field Mapping' },
  { id: 'rationalize', label: 'Rationalize' },
  { id: 'relations', label: 'Relations' },
  { id: 'export', label: 'Export' },
  { id: 'validate', label: 'Validate' },
];


function CsvUploadPrompt({ project, onUploaded, onBack }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const uploadFile = async file => {
    if (!file || uploading) return;
    setUploading(true);
    setError(null);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await apiFetch('/upload-source-csv', { method: 'POST', body: formData });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || data.error || 'The source export could not be uploaded.');
      onUploaded(data);
    } catch (err) {
      setError(err.message);
      setUploading(false);
    }
  };

  const handleDrop = event => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);
    uploadFile(event.dataTransfer.files[0]);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-[#071a26] px-6 py-5 text-white">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-4">
          <button className="text-left focus:outline-none focus:ring-2 focus:ring-[#74d6d1]" onClick={onBack}>
            <div className="text-sm font-semibold text-[#74d6d1]">← All projects</div>
            <div className="mt-1 text-lg font-semibold">{project?.name || 'Migration project'}</div>
          </button>
          <UserButton afterSignOutUrl={window.location.href} />
        </div>
      </header>
      <main className="mx-auto grid min-h-[calc(100vh-84px)] max-w-5xl items-center px-6 py-12">
        <div className="grid overflow-hidden rounded-xl border border-slate-200 bg-white lg:grid-cols-[0.85fr_1.15fr]">
          <div className="bg-[#071a26] p-8 text-white lg:p-10">
            <p className="text-sm font-semibold text-[#74d6d1]">First step</p>
            <h1 className="mt-3 text-3xl font-semibold tracking-[-0.03em]">Add the source DAM export</h1>
            <p className="mt-4 max-w-md text-sm leading-6 text-white/75">
              The file will be parsed, profiled, and audited before any mapping or rationalization work begins. Original source values remain unchanged.
            </p>
            <ul className="mt-7 space-y-3 text-sm text-white/85">
              <li>CSV and Excel (.xlsx)</li>
              <li>Any structured DAM metadata export</li>
              <li>Up to 1 GB by default</li>
            </ul>
          </div>
          <div className="p-8 lg:p-10">
            <h2 className="text-xl font-semibold text-[#071a26]">Choose an export</h2>
            <p className="mt-2 text-sm leading-6 text-slate-600">Large files may take a few minutes to validate. Keep this window open until the source audit appears.</p>
            <label
              className={`mt-6 flex min-h-56 cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed p-8 text-center transition ${
                uploading
                  ? 'cursor-wait border-slate-200 bg-slate-50'
                  : isDragOver
                  ? 'border-[#2a9d98] bg-cyan-50'
                  : 'border-slate-300 hover:border-[#2a9d98] hover:bg-cyan-50/50'
              }`}
              onDrop={handleDrop}
              onDragOver={event => { event.preventDefault(); event.stopPropagation(); }}
              onDragEnter={event => { event.preventDefault(); setIsDragOver(true); }}
              onDragLeave={event => { event.preventDefault(); setIsDragOver(false); }}
            >
              <span className="text-base font-semibold text-slate-800">
                {uploading ? 'Validating source export…' : isDragOver ? 'Drop to begin' : 'Drag a file here or choose from your computer'}
              </span>
              <span className="mt-2 text-sm text-slate-600">.csv or .xlsx</span>
              <input type="file" accept=".csv,.xlsx" className="hidden" disabled={uploading} onChange={event => uploadFile(event.target.files[0])} />
            </label>
            {error && <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" role="alert">{error}</div>}
          </div>
        </div>
      </main>
    </div>
  );
}


function NavigationModal({ unreviewedCount, onProceed, onCancel }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <button className="absolute inset-0 bg-black/45" onClick={onCancel} aria-label="Close dialog" />
      <div className="relative w-full max-w-md rounded-xl bg-white p-7 shadow-xl" role="dialog" aria-modal="true" aria-labelledby="unreviewed-title">
        <h2 id="unreviewed-title" className="text-lg font-semibold text-slate-900">{unreviewedCount.toLocaleString()} asset{unreviewedCount !== 1 ? 's' : ''} are unreviewed</h2>
        <p className="mt-3 text-sm leading-6 text-slate-600">These assets will not be included in the Frontify export. Return to review them, or proceed and export only the Keep assets.</p>
        <div className="mt-6 flex justify-end gap-3">
          <button onClick={onCancel} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Return to review</button>
          <button onClick={onProceed} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700">Proceed to Export</button>
        </div>
      </div>
    </div>
  );
}


function ProjectDetailsEditor({ project, onSaved, onCancel }) {
  const [name, setName] = useState(project?.name || '');
  const [clientName, setClientName] = useState(project?.client_name || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const save = async event => {
    event.preventDefault();
    if (!name.trim() || saving) return;
    setSaving(true);
    setError(null);
    try {
      const response = await apiFetch('/project', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim(), client_name: clientName.trim() }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Project details could not be saved.');
      onSaved(data);
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="border-b border-slate-200 bg-cyan-50 px-6 py-4">
      <div className="mx-auto grid max-w-7xl gap-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] md:items-end">
        <label className="text-sm font-semibold text-slate-700">Project name<input autoFocus className="mt-1.5 w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 font-normal outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200" value={name} onChange={event => setName(event.target.value)} /></label>
        <label className="text-sm font-semibold text-slate-700">Client<input className="mt-1.5 w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 font-normal outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200" value={clientName} onChange={event => setClientName(event.target.value)} /></label>
        <div className="flex gap-2"><button type="button" onClick={onCancel} className="rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700">Cancel</button><button type="submit" disabled={!name.trim() || saving} className="rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-45">{saving ? 'Saving…' : 'Save details'}</button></div>
        {error && <div className="text-sm text-red-700 md:col-span-3" role="alert">{error}</div>}
      </div>
    </form>
  );
}


function AppShell({ onBack }) {
  const [activeTab, setActiveTab] = useState('audit');
  const [project, setProject] = useState(null);
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [needsCsv, setNeedsCsv] = useState(false);
  const [error, setError] = useState(null);
  const [unreviewedCount, setUnreviewedCount] = useState(0);
  const [pendingTab, setPendingTab] = useState(null);
  const [tabStatus, setTabStatus] = useState({});
  const [editingProject, setEditingProject] = useState(false);

  const loadProject = useCallback(async () => {
    const response = await apiFetch('/project');
    const data = await response.json();
    if (!response.ok) throw new Error(data.message || 'The project could not be loaded.');
    setProject(data);
    return data;
  }, []);

  const loadTabStatus = useCallback(() => {
    apiFetch('/tab-status')
      .then(response => response.ok ? response.json() : {})
      .then(data => { if (!data.error) setTabStatus(data); })
      .catch(() => {});
  }, []);

  const loadAudit = useCallback(() => {
    setLoading(true);
    setNeedsCsv(false);
    setError(null);
    apiFetch('/audit')
      .then(response => {
        if (response.status === 404) {
          setNeedsCsv(true);
          setLoading(false);
          return null;
        }
        if (!response.ok) throw new Error(`The source audit could not be loaded (${response.status}).`);
        return response.json();
      })
      .then(data => {
        if (data === null) return;
        setAudit(data);
        setLoading(false);
      })
      .catch(err => { setError(err.message); setLoading(false); });
  }, []);

  useEffect(() => {
    loadProject().catch(err => { setError(err.message); setLoading(false); });
    loadAudit();
    loadTabStatus();
  }, [loadAudit, loadProject, loadTabStatus]);

  useEffect(() => { loadTabStatus(); }, [activeTab, loadTabStatus]);

  useEffect(() => {
    const prevent = event => event.preventDefault();
    document.addEventListener('dragover', prevent);
    document.addEventListener('drop', prevent);
    return () => {
      document.removeEventListener('dragover', prevent);
      document.removeEventListener('drop', prevent);
    };
  }, []);

  const handleTabClick = useCallback(tabId => {
    if (activeTab === 'rationalize' && (tabId === 'export' || tabId === 'validate') && unreviewedCount > 0) {
      setPendingTab(tabId);
    } else {
      setActiveTab(tabId);
    }
  }, [activeTab, unreviewedCount]);

  const handleSourceChanged = useCallback(result => {
    setActiveTab('audit');
    setNeedsCsv(false);
    setError(null);
    setAudit(result.audit || null);
    setUnreviewedCount(0);
    setTabStatus({ audit: 'complete' });
    loadProject().catch(err => setError(err.message));
    if (!result.audit) loadAudit();
  }, [loadAudit, loadProject]);

  if (needsCsv) {
    return <CsvUploadPrompt project={project} onUploaded={handleSourceChanged} onBack={onBack} />;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="h-28 animate-pulse bg-[#071a26]" />
        <div className="mx-auto max-w-7xl space-y-5 px-6 py-8">
          <div className="h-8 w-64 animate-pulse rounded bg-slate-200" />
          <div className="h-36 animate-pulse rounded-xl bg-white" />
          <div className="h-64 animate-pulse rounded-xl bg-white" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 px-6">
        <div className="w-full max-w-md rounded-xl border border-red-200 bg-white p-7">
          <h1 className="text-lg font-semibold text-red-800">This project could not be opened</h1>
          <p className="mt-2 text-sm leading-6 text-slate-600">{error}</p>
          <div className="mt-5 flex gap-3"><button onClick={onBack} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold">All projects</button><button onClick={loadAudit} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white">Try again</button></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-[#071a26] px-6 pt-5 text-white">
        <div className="mx-auto max-w-7xl">
          <div className="flex flex-col justify-between gap-4 pb-5 sm:flex-row sm:items-center">
            <div className="min-w-0">
              <button className="text-sm font-semibold text-[#74d6d1] focus:outline-none focus:ring-2 focus:ring-[#74d6d1]" onClick={onBack}>← All projects</button>
              <div className="mt-1 flex min-w-0 items-center gap-3"><h1 className="truncate text-xl font-semibold">{project?.name || 'Migration project'}</h1><button onClick={() => setEditingProject(value => !value)} className="shrink-0 text-xs font-semibold text-white/65 underline decoration-white/30 underline-offset-4 hover:text-white">Edit details</button></div>
              <p className="mt-0.5 truncate text-sm text-white/65">{project?.client_name || 'Internal project'}</p>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right text-sm text-white/70"><strong className="block text-white">{(audit?.total_assets || 0).toLocaleString()}</strong> assets loaded</div>
              <UserButton afterSignOutUrl={window.location.href} />
            </div>
          </div>
          <nav className="flex gap-1 overflow-x-auto" aria-label="Migration workflow">
            {TABS.map(tab => {
              const isActive = activeTab === tab.id;
              const state = tabStatus[tab.id] || 'default';
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabClick(tab.id)}
                  className={`whitespace-nowrap rounded-t-lg px-4 py-3 text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-inset focus:ring-[#74d6d1] ${isActive ? 'bg-slate-50 text-[#071a26]' : 'text-white/70 hover:bg-white/10 hover:text-white'}`}
                >
                  {tab.label}
                  {!isActive && state === 'complete' && <span className="ml-1.5 text-[#74d6d1]" aria-label="complete">✓</span>}
                  {!isActive && state === 'blocked' && <span className="ml-1.5 text-amber-300" aria-label="needs attention">!</span>}
                </button>
              );
            })}
          </nav>
        </div>
      </header>

      <ProjectActivityBar onNavigate={setActiveTab} />
      {editingProject && <ProjectDetailsEditor project={project} onSaved={data => { setProject(data); setEditingProject(false); }} onCancel={() => setEditingProject(false)} />}
      {project?.source && <SourceControl project={project} onSourceChanged={handleSourceChanged} />}

      <main className="mx-auto max-w-7xl px-6 py-7">
        {activeTab === 'audit' && <Dashboard audit={audit} />}
        {activeTab === 'mapping' && <FieldMapping />}
        {activeTab === 'rationalize' && <RationalizationWorkbench onUnreviewedCountChange={setUnreviewedCount} />}
        {activeTab === 'relations' && <RelationsAuthoring />}
        {activeTab === 'export' && <IngestionExport />}
        {activeTab === 'validate' && <PostIngestionValidation />}
      </main>

      {pendingTab && (
        <NavigationModal
          unreviewedCount={unreviewedCount}
          onProceed={() => { setActiveTab(pendingTab); setPendingTab(null); }}
          onCancel={() => setPendingTab(null)}
        />
      )}
    </div>
  );
}


function ProjectRouter() {
  const [activeProject, setActiveProject] = useState(() => getProjectId());
  const [showWorkspace, setShowWorkspace] = useState(() => !getProjectId());

  const openProject = useCallback(projectId => {
    setProjectId(projectId);
    setActiveProject(projectId);
    setShowWorkspace(false);
  }, []);

  if (showWorkspace || !activeProject) {
    return <ProjectWorkspace onOpen={openProject} />;
  }
  return <AppShell key={activeProject} onBack={() => setShowWorkspace(true)} />;
}


export default function App() {
  return (
    <>
      <SignedOut><RedirectToSignIn /></SignedOut>
      <SignedIn><AuthenticatedApp /></SignedIn>
    </>
  );
}


function AuthenticatedApp() {
  const { getToken } = useAuth();
  configureApiAuth(getToken);
  return <ProjectRouter />;
}
